import type { CatalogFinding, CatalogValidationError, DeadlineChangeConfirmation } from "@/lib/api/generated/casedb";
import { DEADLINE_CHANGE_REQUIRES_CONFIRMATION } from "@/lib/deadlines/deadlineChangeLabels";
import { catalogFieldKey } from "./catalogSchemaIndex";

export const CATALOG_ERROR_CODES = {
  validationFailed: "VALIDATION_FAILED",
  staleRevision: "STALE_REVISION",
  caseLocked: "CASE_LOCKED",
  lockNotHeld: "LOCK_NOT_HELD",
  deadlineChangeRequiresConfirmation: DEADLINE_CHANGE_REQUIRES_CONFIRMATION,
} as const;

/**
 * What went wrong with a catalog request, in terms a component can act on.
 *
 * Components never see an Axios error. The status, the server's code and its findings are the
 * three things the UI actually branches on, and reaching into `error.response.data` at each call
 * site is how one of those branches ends up missing.
 */
/** A follow-up refusal names the arrival and the item it is about, which a section finding cannot. */
export interface FollowupFinding {
  followup_id: number;
  item_id: number;
  field_id: string;
  message: string;
}

export class CatalogApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly findings: CatalogFinding[],
    message: string,
    readonly followupFindings: FollowupFinding[] = [],
    /** The changes of a save that changes a shown due date (decision 16); the server saved nothing. */
    readonly deadlineChange?: DeadlineChangeConfirmation,
  ) {
    super(message);
    this.name = "CatalogApiError";
  }

  /** The write was built on a case revision someone else has already moved past. */
  get isStaleRevision(): boolean {
    return this.status === 409 && this.code === CATALOG_ERROR_CODES.staleRevision;
  }

  /** The values were refused; `findings` says why, field by field. */
  get isValidationFailure(): boolean {
    return this.status === 422;
  }

}

interface AxiosLikeError {
  response?: { status?: number; data?: { errorCode?: string; message?: string; data?: unknown } };
  message?: string;
  status?: number;
  code?: string;
  findings?: CatalogFinding[];
}

/** Translates whatever the transport threw into one shape, losing nothing the UI needs. */
export function toCatalogApiError(error: unknown): CatalogApiError {
  if (error instanceof CatalogApiError) return error;
  const axiosLike = error as AxiosLikeError;
  const status = axiosLike?.response?.status ?? axiosLike?.status
    ?? (Array.isArray(axiosLike?.findings) ? 422 : 0);
  const body = axiosLike?.response?.data;
  // The follow-up writer answers with a list of failures, each naming its arrival and item; the
  // catalog validator answers with one object carrying `findings`.
  const followup = Array.isArray(body?.data) ? followupFindingsOf(body.data) : [];
  const payload = Array.isArray(body?.data) ? undefined : body?.data as CatalogValidationError | undefined;
  const deadlineChange = body?.errorCode === DEADLINE_CHANGE_REQUIRES_CONFIRMATION
    ? body.data as DeadlineChangeConfirmation : undefined;

  return new CatalogApiError(
    status,
    payload?.code ?? body?.errorCode ?? axiosLike?.code
      ?? (Array.isArray(axiosLike?.findings) ? CATALOG_ERROR_CODES.validationFailed : "UNKNOWN"),
    payload?.findings ?? axiosLike?.findings ?? [],
    // No invented message: a caller with a better fallback ("Save failed…") uses it when this is empty.
    body?.message ?? axiosLike?.message ?? "",
    followup,
    deadlineChange,
  );
}

function followupFindingsOf(failures: unknown[]): FollowupFinding[] {
  return failures.flatMap((raw) => {
    const failure = raw as { followup_id?: number; item_id?: number; field_id?: string; message?: string };
    if (typeof failure.item_id !== "number" || typeof failure.followup_id !== "number") return [];
    return [{ followup_id: failure.followup_id, item_id: failure.item_id,
      field_id: failure.field_id ?? "", message: failure.message ?? "This follow-up decision was refused." }];
  });
}

/** Findings keyed the way a control is addressed: one row, one field. */
export function findingKey(finding: CatalogFinding): string {
  return catalogFieldKey(finding.record_id ?? undefined, finding.field_id ?? "");
}
